import { useRef, useState, useCallback, useEffect } from "react";
import { Platform } from "react-native";

const DRAG_THRESHOLD = 5; // Minimum pixels to consider it a drag
const MOMENTUM_MULTIPLIER = 5; // Multiplier for momentum strength
const DECELERATION_FACTOR = 0.92; // How quickly momentum decelerates
const MIN_VELOCITY = 0.2; // Minimum velocity before stopping
const MIN_VELOCITY_FOR_MOMENTUM = 0.1; // Minimum velocity to trigger momentum

export const useDragScroll = () => {
  // Only isDragging and hasDragged need to be state for context sharing
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  // Everything else can be refs since they don't trigger re-renders
  const startX = useRef(0);
  const scrollStartX = useRef(0);
  const velocity = useRef(0);
  const lastMoveTime = useRef(0);
  const lastMoveX = useRef(0);
  const hasDraggedRef = useRef(false);
  const animationRef = useRef<number | null>(null);

  // Apply momentum scrolling after mouse up.
  // The recursive scheduling reads the implementation from a ref so the
  // function never references its own binding before it is declared.
  const applyMomentumImpl = useRef<
    (scrollElement: any, currentVelocity: number) => void
  >(() => {});
  useEffect(() => {
    applyMomentumImpl.current = (
      scrollElement: any,
      currentVelocity: number,
    ) => {
      if (Math.abs(currentVelocity) < MIN_VELOCITY) {
        return; // Stop when velocity is negligible
      }

      const newVelocity = currentVelocity * DECELERATION_FACTOR;
      scrollElement.scrollLeft += currentVelocity;

      animationRef.current = requestAnimationFrame(() => {
        applyMomentumImpl.current(scrollElement, newVelocity);
      });
    };
  }, []);
  const applyMomentum = useCallback(
    (scrollElement: any, currentVelocity: number) => {
      applyMomentumImpl.current(scrollElement, currentVelocity);
    },
    [],
  );

  const handleMouseDown = useCallback((e: any) => {
    if (Platform.OS !== "web") return;

    // Cancel any ongoing momentum animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setIsDragging(true);
    const x = e.clientX || e.pageX;
    startX.current = x;
    lastMoveX.current = x;
    lastMoveTime.current = Date.now();
    velocity.current = 0;
    setHasDragged(false);
    hasDraggedRef.current = false;

    // Get current scroll position
    const scrollElement =
      e.currentTarget?.querySelector('[data-testid="flat-list"]') ||
      e.currentTarget;
    scrollStartX.current = scrollElement?.scrollLeft || 0;

    // Prevent text selection while dragging
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: any) => {
      if (!isDragging || Platform.OS !== "web") return;

      const currentX = e.clientX || e.pageX;
      const deltaX = startX.current - currentX;

      // Track drag distance to determine if this is a drag vs click
      const dragDistance = Math.abs(deltaX);

      // Mark as dragged if we've moved beyond the threshold
      if (dragDistance > DRAG_THRESHOLD && !hasDraggedRef.current) {
        setHasDragged(true);
        hasDraggedRef.current = true;
      }

      // Calculate velocity for momentum
      const now = Date.now();
      const timeDelta = now - lastMoveTime.current;
      if (timeDelta > 0) {
        const moveDelta = currentX - lastMoveX.current;
        velocity.current = (-moveDelta / timeDelta) * MOMENTUM_MULTIPLIER;
      }
      lastMoveX.current = currentX;
      lastMoveTime.current = now;

      const scrollElement =
        e.currentTarget?.querySelector('[data-testid="flat-list"]') ||
        e.currentTarget;
      if (scrollElement) {
        scrollElement.scrollLeft = scrollStartX.current + deltaX;
      }

      e.preventDefault();
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(
    (e: any) => {
      if (Platform.OS !== "web") return;
      setIsDragging(false);

      // If we dragged, prevent the mouseup from propagating
      if (hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        // Reset the flag after a short delay to allow onPress to check it
        setTimeout(() => {
          setHasDragged(false);
          hasDraggedRef.current = false;
        }, 100);
      }

      // Start momentum animation if there's velocity
      if (Math.abs(velocity.current) > MIN_VELOCITY_FOR_MOMENTUM) {
        const scrollElement =
          e.currentTarget?.querySelector('[data-testid="flat-list"]') ||
          e.currentTarget;
        if (scrollElement) {
          applyMomentum(scrollElement, velocity.current);
        }
      }
    },
    [applyMomentum],
  );

  const handleMouseLeave = useCallback(
    (e: any) => {
      if (Platform.OS !== "web") return;

      // Only handle if we're actually dragging
      if (!isDragging) return;

      setIsDragging(false);

      // Reset drag state when leaving
      if (hasDraggedRef.current) {
        setTimeout(() => {
          setHasDragged(false);
          hasDraggedRef.current = false;
        }, 100);
      }

      // Start momentum animation if there's velocity
      if (Math.abs(velocity.current) > MIN_VELOCITY_FOR_MOMENTUM) {
        const scrollElement =
          e.currentTarget?.querySelector('[data-testid="flat-list"]') ||
          e.currentTarget;
        if (scrollElement) {
          applyMomentum(scrollElement, velocity.current);
        }
      }
    },
    [isDragging, applyMomentum],
  );

  const handleClick = useCallback((e: any) => {
    if (Platform.OS !== "web") return;

    if (hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      // Don't reset here, let mouseUp handle it
      return false;
    }
  }, []);

  // Global click prevention and cleanup when dragging
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const preventClickAfterDrag = (e: MouseEvent) => {
      if (hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Reset the flag
        hasDraggedRef.current = false;
        setHasDragged(false);

        return false;
      }
    };

    // Global mouseup to handle releasing outside the component
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);

        // Reset drag flags after a short delay
        if (hasDraggedRef.current) {
          setTimeout(() => {
            setHasDragged(false);
            hasDraggedRef.current = false;
          }, 100);
        }
      }
    };

    // Add listeners
    document.addEventListener("click", preventClickAfterDrag, true);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("click", preventClickAfterDrag, true);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging]);

  // Web-specific props for drag functionality
  const webProps =
    Platform.OS === "web"
      ? {
          onMouseDownCapture: handleMouseDown,
          onMouseDown: handleMouseDown,
          onMouseMove: handleMouseMove,
          onMouseUp: handleMouseUp,
          onMouseLeave: handleMouseLeave,
          onClickCapture: handleClick,
          onClick: handleClick,
          style: {
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: isDragging ? "none" : "auto",
            WebkitUserSelect: isDragging ? "none" : "auto",
          } as any,
        }
      : {};

  return {
    isDragging,
    hasDragged,
    webProps,
  };
};